import * as Sentry from "@sentry/react";

/**
 * Long-edge target, derived in the practice dashboard against real production attachments: the
 * dominant source is 3264x2448 (8 MP), so a target above 3000 would barely shrink anything, and
 * below 2400 a photographed Rezept drops under the threshold where stamp digits stay readable
 * (PF-5488).
 */
export const MAX_IMAGE_EDGE_PX = 2400;

/** Quality is not the legibility axis: a photo of paper has no hard edges for JPEG to ring against. */
const JPEG_QUALITY = 0.75;
const MAX_SIZE_RATIO = 0.9;
const RE_ENCODE_MIN_BYTES = 1_000_000;
const PNG_CONVERT_MIN_BYTES = 5_000_000;

/**
 * Shrinks a photo before upload. Returns the ORIGINAL file unchanged whenever the input is not a
 * decodable image, is already small enough, or would not get meaningfully smaller. Never rejects.
 *
 * On success the result is a JPEG, which also means the HEIC branch in the caller stops firing and
 * the `convertAndStoreAsset` round trip is skipped. When decoding fails (HEIC outside Safari) the
 * original passes through and that server-side path runs unchanged.
 */
export const downscaleImageFile = async (file: File): Promise<File> => {
  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/png" && file.size < PNG_CONVERT_MIN_BYTES) return file;

  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    const scale = Math.min(
      1,
      MAX_IMAGE_EDGE_PX / Math.max(bitmap.width, bitmap.height),
    );
    if (scale === 1 && file.size < RE_ENCODE_MIN_BYTES) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    if (!context) {
      bitmap.close();
      return file;
    }
    context.imageSmoothingQuality = "high";
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    if (!blob || blob.size > file.size * MAX_SIZE_RATIO) return file;

    return new File([blob], file.name, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "downscale",
      message: "downscale skipped",
      data: { type: file.type, size: file.size, reason: String(error) },
    });
    return file;
  }
};
